import React, { useEffect, useState } from 'react';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import FormDialog from '../../common/FormDialog';

const EMPTY_ROW = () => ({ rating_code: '', rating_name: '', rating_value: '', description: '' });

const BulkConductRatingDialog = ({ open, onClose, onSubmit, saving, isMobile = false, existingRatings = [] }) => {
  const [rows, setRows] = useState([EMPTY_ROW()]);

  useEffect(() => { if (open) setRows([EMPTY_ROW()]); }, [open]);

  const update = (i, field, value) =>
    setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  // collect all used values: existing ratings + other rows in the table
  const isDuplicateValue = (rowIndex) => {
    const val = rows[rowIndex].rating_value;
    if (val === '') return false;
    const inExisting = existingRatings.some((r) => Number(r.rating_value) === Number(val));
    const inOtherRows = rows.some((r, i) => i !== rowIndex && r.rating_value !== '' && Number(r.rating_value) === Number(val));
    return inExisting || inOtherRows;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const valid = rows.filter((r) => r.rating_name.trim() && r.rating_value !== '');
    if (!valid.length) return;
    if (rows.some((_, i) => isDuplicateValue(i))) return;
    onSubmit(valid, () => setRows([EMPTY_ROW()]));
  };

  const canSave = rows.some((r) => r.rating_name.trim() && r.rating_value !== '') &&
    !rows.some((_, i) => isDuplicateValue(i));

  return (
    <FormDialog open={open} onClose={onClose} onSubmit={handleSubmit}
      title="Bulk Add Conduct Ratings" saving={saving} disabled={!canSave}
      isMobile={isMobile} maxWidth="lg">
      <Box sx={{ overflowX: 'auto', pt: 1 }}>
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell>Rating Name *</TableCell>
              <TableCell>Value *</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Description</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                <TableCell>
                  <TextField size="small" value={row.rating_name}
                    onChange={(e) => update(i, 'rating_name', e.target.value)}
                    placeholder="e.g. Excellent" sx={{ minWidth: 150 }} required />
                </TableCell>
                <TableCell>
                  <TextField size="small" type="number" value={row.rating_value}
                    onChange={(e) => update(i, 'rating_value', e.target.value)}
                    inputProps={{ min: 1 }} sx={{ width: 80 }} required
                    error={isDuplicateValue(i)}
                    helperText={isDuplicateValue(i) ? 'Duplicate' : ''} />
                </TableCell>
                <TableCell>
                  <TextField size="small" value={row.rating_code}
                    onChange={(e) => update(i, 'rating_code', e.target.value.toUpperCase())}
                    placeholder="e.g. EXC" inputProps={{ maxLength: 20 }} sx={{ width: 100 }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" value={row.description}
                    onChange={(e) => update(i, 'description', e.target.value)}
                    placeholder="Optional description" sx={{ minWidth: 180 }} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="error"
                    onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}
                    disabled={rows.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ mt: 1 }}>
        <IconButton size="small" color="primary" onClick={() => setRows((p) => [...p, EMPTY_ROW()])}>
          <AddIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>Add row</Typography>
      </Box>
    </FormDialog>
  );
};

export default BulkConductRatingDialog;
